// =============================================================================
// RunsScreen — per-run billing review for external customers (Evo1)
// =============================================================================
// One room + one venue-local month at a time. Every run in the window is
// listed grouped by day; runs the database flags as restart suspects are
// highlighted so the reviewer sees them first. A void is a reversible mark
// with a reason (never a delete), attributed server-side to the operator.
// "Markeer als gefactureerd" freezes the month once the invoice went out.
//
// Partner reports (filed on the Meldingen page by Evoluon staff) show up in
// the Opmerking column with accept / reject: accept voids the run with the
// report as reason, reject keeps it billable. Both take an optional note the
// reporter sees.
//
// Below 720px the table turns into one card per run (styles.css, .runs-table
// media query); the cell classes below are the grid areas.
//
// Gated by booking_manage_external_runs (route + every RPC).
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  listBillingRooms,
  listBillingRuns,
  markRunsInvoiced,
  reviewRunReport,
  unvoidRun,
  voidRun,
  type BillingRoom,
  type BillingRun,
  type BillingRunFeed,
  type RunStatus,
} from '../lib/runs';
import { formatError } from '../lib/errors';
import { formatVenueDate, formatVenueTime, venueMonthBounds, venueYearMonth } from '../lib/time';
import { RunReportCard } from '../components/RunReportCard';

const STATUS_LABEL: Record<RunStatus, string> = {
  billable: 'Facturabel',
  invoiced: 'Gefactureerd',
  voided:   'Niet factureren',
  test:     'Test',
};

const STATUS_CLASS: Record<RunStatus, string> = {
  billable: 'voucher-status-ok',
  invoiced: 'voucher-status-off',
  voided:   'voucher-status-warn',
  test:     'voucher-status-off',
};

function formatDuration(run: BillingRun): string {
  if (run.total_time_ms != null && run.total_time_ms > 0) {
    return `${Math.round(run.total_time_ms / 60000)} min`;
  }
  if (run.completed_at) return '—';
  return 'niet afgerond';
}

function csvEscape(v: string | number | null | undefined): string {
  const s = v == null ? '' : String(v);
  return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

type ReviewDecision = 'accepted' | 'rejected';

export function RunsScreen() {
  const [rooms, setRooms] = useState<BillingRoom[]>([]);
  const [roomId, setRoomId] = useState<string>('');
  const [month, setMonth] = useState<string>(() => venueYearMonth());
  const [feed, setFeed] = useState<BillingRunFeed | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [voidingId, setVoidingId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [reviewNote, setReviewNote] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  // Room picker: the server only lists rooms in external_billing_rooms.
  useEffect(() => {
    let cancelled = false;
    void listBillingRooms()
      .then((rs) => {
        if (cancelled) return;
        setRooms(rs);
        setRoomId((cur) => cur || rs[0]?.room_id || '');
      })
      .catch((e) => {
        if (!cancelled) setError(formatError(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const bounds = useMemo(() => (month ? venueMonthBounds(month) : null), [month]);

  const load = useCallback(async () => {
    if (!roomId || !bounds) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setFeed(await listBillingRuns(roomId, bounds.startUtc, bounds.endUtc));
      setError(null);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [roomId, bounds]);

  useEffect(() => {
    void load();
  }, [load]);

  const room = rooms.find((r) => r.room_id === roomId) ?? null;

  async function confirmVoid(run: BillingRun) {
    const r = reason.trim();
    if (!r) return;
    setBusyId(run.id);
    try {
      await voidRun(run.id, r);
      setVoidingId(null);
      setReason('');
      setNotice(null);
      await load();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusyId(null);
    }
  }

  async function restore(run: BillingRun) {
    setBusyId(run.id);
    try {
      await unvoidRun(run.id);
      await load();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusyId(null);
    }
  }

  async function review(run: BillingRun, decision: ReviewDecision) {
    if (!run.report) return;
    setBusyId(run.id);
    try {
      await reviewRunReport(run.report.id, decision, reviewNote.trim() || null);
      setReviewNote('');
      setNotice(null);
      await load();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusyId(null);
    }
  }

  async function markInvoiced() {
    if (!roomId || !bounds || !feed) return;
    const n = feed.summary.billable;
    if (n === 0) return;
    const ok = window.confirm(
      `${n} facturabele run${n === 1 ? '' : 's'} in ${month} markeren als gefactureerd? ` +
        'Runs die je daarna nog op "Niet factureren" zet, blijven zichtbaar.',
    );
    if (!ok) return;
    setBusyId('__month');
    try {
      const count = await markRunsInvoiced(roomId, bounds.startUtc, bounds.endUtc);
      setNotice(`${count} run${count === 1 ? '' : 's'} gemarkeerd als gefactureerd.`);
      await load();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusyId(null);
    }
  }

  function exportCsv() {
    if (!feed || !room) return;
    const header = ['datum', 'tijd', 'pc', 'beleving', 'duur_min', 'status', 'reden', 'melding', 'run_id'];
    const lines = feed.rows.map((r) =>
      [
        formatVenueDate(r.started_at),
        formatVenueTime(r.started_at),
        r.pc_name ?? '',
        r.experience_name ?? '',
        r.total_time_ms != null ? Math.round(r.total_time_ms / 60000) : '',
        STATUS_LABEL[r.status],
        r.billing_void_reason ?? '',
        r.report ? `${r.report.status}: ${r.report.message}` : '',
        r.id,
      ]
        .map(csvEscape)
        .join(';'),
    );
    const blob = new Blob(['﻿' + [header.join(';'), ...lines].join('\n')], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `runs-${room.room_name}-${month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Group rows by venue day for the day-header rows.
  const grouped = useMemo(() => {
    const out: { day: string; rows: BillingRun[] }[] = [];
    for (const r of feed?.rows ?? []) {
      const day = formatVenueDate(r.started_at);
      const last = out[out.length - 1];
      if (last && last.day === day) last.rows.push(r);
      else out.push({ day, rows: [r] });
    }
    return out;
  }, [feed]);

  const s = feed?.summary;

  return (
    <div className="screen payments-screen runs-screen">
      <h1 className="payments-title">Runs</h1>

      <div className="payments-filters">
        <label className="walkin-field payments-filter">
          <span>Klant / ruimte</span>
          <select value={roomId} onChange={(e) => setRoomId(e.target.value)}>
            {rooms.length === 0 ? <option value="">—</option> : null}
            {rooms.map((r) => (
              <option key={r.room_id} value={r.room_id}>
                {r.customer_name} · {r.room_name}
              </option>
            ))}
          </select>
        </label>
        <label className="walkin-field payments-filter">
          <span>Maand</span>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        </label>
        <div className="runs-actions">
          <button
            className="btn-link"
            disabled={!feed || feed.rows.length === 0}
            onClick={exportCsv}
          >
            Exporteer CSV
          </button>
          <button
            className="btn-link"
            disabled={!feed || feed.summary.billable === 0 || busyId === '__month'}
            onClick={() => void markInvoiced()}
          >
            Markeer maand als gefactureerd
          </button>
        </div>
      </div>

      {s ? (
        <div className="payments-summary muted">
          <strong>{s.billable}</strong> facturabel
          {s.invoiced > 0 ? <> · {s.invoiced} gefactureerd</> : null}
          {s.voided > 0 ? <> · {s.voided} niet factureren</> : null}
          {s.test > 0 ? <> · {s.test} test</> : null}
          {s.restart_suspects > 0 ? (
            <> · <span className="runs-suspect-count">{s.restart_suspects} mogelijk herstart</span></>
          ) : null}
          {s.open_reports > 0 ? (
            <> · <span className="runs-open-reports">{s.open_reports} open melding{s.open_reports === 1 ? '' : 'en'}</span></>
          ) : null}
          {' '}
          <button className="btn-link" onClick={() => void load()}>Ververs</button>
        </div>
      ) : null}

      {notice ? <div className="muted runs-notice">{notice}</div> : null}
      {error ? <div className="error-card">{error}</div> : null}
      {loading ? <div className="muted">Laden…</div> : null}

      {!loading && feed && feed.rows.length === 0 ? (
        <div className="muted vouchers-empty">Geen runs in deze maand.</div>
      ) : null}

      {feed && feed.rows.length > 0 ? (
        <table className="payments-table runs-table">
          <thead>
            <tr>
              <th>Tijd</th>
              <th>PC</th>
              <th>Beleving</th>
              <th>Duur</th>
              <th>Status</th>
              <th>Opmerking</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {grouped.map((g) => (
              <RunDayGroup
                key={g.day}
                day={g.day}
                rows={g.rows}
                busyId={busyId}
                voidingId={voidingId}
                reason={reason}
                reviewNote={reviewNote}
                onReason={setReason}
                onReviewNote={setReviewNote}
                onStartVoid={(r) => {
                  setVoidingId(r.id);
                  setReason(r.restart_suspect ? 'Bezoeker herstart' : '');
                }}
                onCancelVoid={() => {
                  setVoidingId(null);
                  setReason('');
                }}
                onConfirmVoid={(r) => void confirmVoid(r)}
                onRestore={(r) => void restore(r)}
                onReview={(r, d) => void review(r, d)}
              />
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

interface RowHandlers {
  busyId: string | null;
  voidingId: string | null;
  reason: string;
  reviewNote: string;
  onReason: (v: string) => void;
  onReviewNote: (v: string) => void;
  onStartVoid: (r: BillingRun) => void;
  onCancelVoid: () => void;
  onConfirmVoid: (r: BillingRun) => void;
  onRestore: (r: BillingRun) => void;
  onReview: (r: BillingRun, decision: ReviewDecision) => void;
}

function RunDayGroup(props: RowHandlers & { day: string; rows: BillingRun[] }) {
  const billable = props.rows.filter((r) => r.status === 'billable' || r.status === 'invoiced').length;
  return (
    <>
      <tr className="runs-day-row">
        <td colSpan={7}>
          {props.day} <span className="muted">· {billable} facturabel</span>
        </td>
      </tr>
      {props.rows.map((r) => (
        <RunRow key={r.id} run={r} {...props} />
      ))}
    </>
  );
}

function RunRow({
  run,
  busyId,
  voidingId,
  reason,
  reviewNote,
  onReason,
  onReviewNote,
  onStartVoid,
  onCancelVoid,
  onConfirmVoid,
  onRestore,
  onReview,
}: RowHandlers & { run: BillingRun }) {
  const busy = busyId === run.id;
  const editing = voidingId === run.id;
  const suspect = run.restart_suspect && run.status !== 'voided' && run.status !== 'test';
  const report = run.report;

  return (
    <tr className={suspect ? 'runs-row-suspect' : undefined}>
      <td className="muted runs-cell-time">{formatVenueTime(run.started_at)}</td>
      <td className="runs-cell-pc">{run.pc_name ?? '—'}</td>
      <td className="muted runs-cell-exp">{run.experience_name ?? '—'}</td>
      <td className="runs-cell-dur">
        {formatDuration(run)}
        {run.crash_count > 0 ? (
          <div className="muted voucher-notes">{run.crash_count}× crash</div>
        ) : null}
      </td>
      <td className="runs-cell-status">
        <span className={`voucher-pill ${STATUS_CLASS[run.status]}`}>{STATUS_LABEL[run.status]}</span>
      </td>
      <td className="runs-cell-note">
        {suspect ? (
          <div className="runs-suspect">
            Mogelijk herstart
            {run.minutes_to_next != null ? (
              <span className="muted"> · {run.minutes_to_next} min later opnieuw gestart op deze PC</span>
            ) : null}
          </div>
        ) : null}
        {run.status === 'voided' ? (
          <div className="muted voucher-notes">
            {run.billing_void_reason}
            {run.voided_by_name ? <> · {run.voided_by_name}</> : null}
          </div>
        ) : null}
        {report ? (
          <RunReportCard report={report}>
            {run.status !== 'test' ? (
              <form
                className="runs-review-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  onReview(run, 'accepted');
                }}
              >
                <input
                  type="text"
                  value={reviewNote}
                  placeholder="Notitie voor de melder (optioneel)"
                  onChange={(e) => onReviewNote(e.target.value)}
                  disabled={busy}
                />
                {report.status !== 'accepted' ? (
                  <button type="submit" className="btn-link" disabled={busy}>
                    Accepteer · niet factureren
                  </button>
                ) : null}
                {report.status !== 'rejected' ? (
                  <button
                    type="button"
                    className="btn-link"
                    disabled={busy}
                    onClick={() => onReview(run, 'rejected')}
                  >
                    Wijs af · wel factureren
                  </button>
                ) : null}
              </form>
            ) : null}
          </RunReportCard>
        ) : null}
        {editing ? (
          <form
            className="runs-void-form"
            onSubmit={(e) => {
              e.preventDefault();
              onConfirmVoid(run);
            }}
          >
            <input
              autoFocus
              type="text"
              value={reason}
              placeholder="Reden (verplicht)"
              onChange={(e) => onReason(e.target.value)}
              disabled={busy}
            />
            <button type="submit" className="btn-link" disabled={busy || reason.trim() === ''}>
              Bevestig
            </button>
            <button type="button" className="btn-link" onClick={onCancelVoid} disabled={busy}>
              Annuleer
            </button>
          </form>
        ) : null}
      </td>
      <td className="runs-cell-action">
        {run.status === 'voided' ? (
          <button className="btn-link" disabled={busy} onClick={() => onRestore(run)}>
            Herstel
          </button>
        ) : run.status === 'test' || editing ? null : (
          <button className="btn-link" disabled={busy} onClick={() => onStartVoid(run)}>
            Niet factureren
          </button>
        )}
      </td>
    </tr>
  );
}
